export function timeFromDateType(value) {
  var x = value.replace("PT", "");
  var y = x.split("H");

  return {
    hours: parseInt(y[0]),
    mins: y[1] ? parseInt(y[1].replace("M", "")) : 0,
  };
}

/**
 * combineDateTimeFromTwoDates
 * - dateInput: string (ISO) or Date object
 * - timeInput: string (ISO) or Date object
 * Returns: DateTime string in UTC format "YYYY-MM-DDTHH:mm:ss.sssZ"
 * Note: the timeInput's timezone is ignored; only the time components are used.
 * */
export function combineDateTimeFromTwoDates(dateInput, timeInput) {
  let dateObj = dateInput instanceof Date ? dateInput : new Date(dateInput);
  let timeObj = timeInput instanceof Date ? timeInput : new Date(timeInput);
  const combinedDateTime = new Date(
    dateObj.getFullYear(),
    dateObj.getMonth(),
    dateObj.getDate(),
    timeObj.getHours(),
    timeObj.getMinutes(),
    timeObj.getSeconds(),
    timeObj.getMilliseconds()
  );

  return combinedDateTime.toUTCDateTimeString();
}

/**
 * combineDateAndTimeNoTZ
 * - dateInput: string (ISO) or Date object
 * - timeInput: string (ISO-like with time part and optional timezone, OR a Date object)
 * Returns: "YYYY-MM-DDTHH:mm:ss" (no timezone suffix)
 */
export function combineDateAndTimeNoTZ(dateInput, timeInput) {
  // Helper: pad numbers to 2 digits
  const pad = (n, width = 2) => {
    return String(n).padStart(width, "0");
  };

  // 1) get date part (YYYY-MM-DD) without applying local timezone shifts
  let datePart;
  if (typeof dateInput === "string") {
    // if it's a string like "2025-12-05T05:00:16.154Z" -> take before 'T'
    const tIndex = dateInput.indexOf("T");
    if (tIndex >= 0) datePart = dateInput.slice(0, tIndex);
    else datePart = dateInput; // assume already YYYY-MM-DD
  } else if (dateInput instanceof Date) {
    // use UTC components so we don't get local timezone shifts
    const y = dateInput.getUTCFullYear();
    const m = pad(dateInput.getUTCMonth() + 1);
    const d = pad(dateInput.getUTCDate());
    datePart = `${y}-${m}-${d}`;
  } else {
    throw new Error("Unsupported dateInput type");
  }

  // 2) get time part HH:mm:ss[.sss] WITHOUT timezone info
  let timePart;
  if (typeof timeInput === "string") {
    // Expect something like "1969-12-31T01:00:00-05" or "01:00:00-05" or "01:00:00"
    // If it has 'T', take after it; otherwise assume the string is time-like
    const tIndex = timeInput.indexOf("T");
    const afterT = tIndex >= 0 ? timeInput.slice(tIndex + 1) : timeInput;

    // remove timezone suffix: Z or +HH[:MM] or -HH[:MM] or +/-HH
    // also trim surrounding whitespace
    timePart = afterT.trim().replace(/(Z|[+-]\d{2}(?::\d{2})?)\s*$/, "");

    // If the time part contains milliseconds, keep them; otherwise ensure HH:mm:ss form
    // If user provided '01:00', normalize to '01:00:00'
    const parts = timePart.split(":");
    if (parts.length === 2) {
      timePart = `${pad(parts[0])}:${pad(parts[1])}:00`;
    } else if (parts.length === 3) {
      // keep as-is, but normalize each number (seconds may include .sss)
      const hh = pad(parts[0]);
      const mm = pad(parts[1]);
      // seconds may be "ss" or "ss.mmm"
      const sec = parts[2];
      // ensure seconds integer part is two digits
      const secParts = sec.split(".");
      secParts[0] = pad(secParts[0]);
      timePart =
        secParts.length > 1
          ? `${hh}:${mm}:${secParts[0]}.${secParts[1]}`
          : `${hh}:${mm}:${secParts[0]}`;
    } else {
      // fallback: zero time
      timePart = "00:00:00";
    }
  } else if (timeInput instanceof Date) {
    // If given a Date object, extract UTC time components to avoid local conversion.
    // Note: using a Date object loses the original timezone suffix info; this is a best-effort.
    const hh = pad(timeInput.getUTCHours());
    const mm = pad(timeInput.getUTCMinutes());
    const ss = pad(timeInput.getUTCSeconds());
    const ms = timeInput.getUTCMilliseconds();
    timePart = ms
      ? `${hh}:${mm}:${ss}.${String(ms).padStart(3, "0")}`
      : `${hh}:${mm}:${ss}`;
  } else {
    throw new Error("Unsupported timeInput type");
  }

  // 3) return combined string without timezone suffix
  return `${datePart}T${timePart}`;
}
